import {
  Button,
  Card,
  CardContent,
  DataTable,
  PageHeader,
  SelectField,
  StatusChip,
  StatusTimeline,
  TextareaField,
} from "@jod/ui";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { hasPermission } from "../../features/ops/permissions.ts";
import { useAddIncidentNote, useCreateIncident, useIncidents } from "../../features/ops/queries.ts";
import type { Incident, Severity } from "../../features/ops/types.ts";
import { getStaffPrincipal } from "../../lib/auth-session.ts";

export const Route = createFileRoute("/_app/incidents")({ component: IncidentScreen });

const severities: Severity[] = ["low", "medium", "high", "critical"];

function IncidentScreen() {
  const principal = getStaffPrincipal();
  const canCreate = hasPermission(principal, "incident:create");
  const { data: incidents = [] } = useIncidents();
  const createIncident = useCreateIncident();
  const addNote = useAddIncidentNote();

  const [selectedId, setSelectedId] = useState<string | undefined>(undefined);
  const [showCreate, setShowCreate] = useState(false);
  const [title, setTitle] = useState("");
  const [severity, setSeverity] = useState<Severity>("medium");
  const [description, setDescription] = useState("");
  const [note, setNote] = useState("");

  const selected = incidents.find((incident) => incident.id === selectedId) ?? incidents[0];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Incident log / บันทึกเหตุการณ์"
        subtitle="Owner and floor-manager operational view; append-only note thread."
        actions={
          canCreate ? (
            <Button onClick={() => setShowCreate((value) => !value)}>
              + สร้าง / Create incident
            </Button>
          ) : undefined
        }
      />

      {showCreate ? (
        <Card>
          <CardContent className="space-y-4 p-5">
            <TextareaField id="incTitle" label="Title" value={title} onChange={setTitle} rows={1} />
            <SelectField
              id="incSeverity"
              label="Severity"
              value={severity}
              onChange={(value) => setSeverity(value as Severity)}
              options={severities.map((value) => ({ value, label: value }))}
            />
            <TextareaField
              id="incDescription"
              label="Description"
              value={description}
              onChange={setDescription}
              placeholder="What happened, who was involved (operational identifiers only), what was done"
            />
            <div className="flex gap-2">
              <Button
                disabled={title.trim().length === 0 || description.trim().length === 0}
                onClick={() =>
                  createIncident.mutate(
                    { title: title.trim(), severity, description: description.trim() },
                    {
                      onSuccess: (incident) => {
                        setSelectedId(incident.id);
                        setShowCreate(false);
                        setTitle("");
                        setDescription("");
                      },
                    },
                  )
                }
              >
                Create
              </Button>
              <Button variant="ghost" onClick={() => setShowCreate(false)}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : null}

      <DataTable<Incident>
        rows={incidents}
        getRowKey={(row) => row.id}
        caption="Incidents"
        columns={[
          {
            key: "title",
            header: "Title",
            cell: (row) => (
              <button
                type="button"
                className="text-left underline-offset-2 hover:underline"
                onClick={() => setSelectedId(row.id)}
              >
                {row.title}
              </button>
            ),
          },
          {
            key: "severity",
            header: "Severity",
            cell: (row) => (
              <StatusChip
                tone={
                  row.severity === "critical"
                    ? "danger"
                    : row.severity === "high"
                      ? "warning"
                      : "info"
                }
              >
                {row.severity}
              </StatusChip>
            ),
          },
          { key: "status", header: "State", cell: (row) => row.status },
          { key: "summary", header: "Summary", cell: (row) => row.summary },
        ]}
      />
      {selected ? (
        <Card>
          <CardContent className="space-y-4 p-5">
            <h2 className="font-display font-semibold text-lg">
              Selected detail: {selected.title}
            </h2>
            <StatusTimeline
              items={selected.notes.map((entry) => ({
                id: entry.id,
                label: entry.author,
                timestamp: entry.createdAt,
                description: entry.note,
              }))}
            />
            <TextareaField
              id="incidentNote"
              label="Add note (append-only) / เพิ่มบันทึก"
              value={note}
              onChange={setNote}
              rows={2}
            />
            <Button
              disabled={note.trim().length === 0}
              onClick={() =>
                addNote.mutate(
                  { incidentId: selected.id, note: note.trim() },
                  { onSuccess: () => setNote("") },
                )
              }
            >
              + เพิ่มบันทึก / Add note
            </Button>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}
